import { DEFAULT_PHONE_COUNTRY, normalizePhone } from './phone.js';

/**
 * Every event BTSis may tell somebody about, named once — PRD §7,
 * docs/plans/07-notifications-and-reporting.md §7.1. A trigger names an entry
 * of this catalogue and nothing else, so an event that reaches a person by two
 * routes reads the same on both and a channel added later is a driver rather
 * than a change at the call site.
 */

/**
 * `in_app` is the row itself: it is delivered by being written, and has no
 * driver call, no queue and no provider. The other four leave the building.
 * `push` has no configured provider yet, so its driver refuses and the failure
 * is on the operator's screen rather than silent — §7.1's open seam.
 */
export const NOTIFICATION_CHANNELS = ['email', 'sms', 'whatsapp', 'in_app', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/**
 * The channels a donor must have agreed to before anything leaves on them, and
 * the only ones a `donor_channel_consents` row may name. `in_app` is not among
 * them — the donor area is a place the donor came to, and a notice waiting
 * there is not a message sent to an address — and `push` has no provider and no
 * address a donor could give, so consenting to it would consent to nothing.
 */
export const CONSENTABLE_NOTIFICATION_CHANNELS = ['email', 'sms', 'whatsapp'] as const;
export type ConsentableNotificationChannel = (typeof CONSENTABLE_NOTIFICATION_CHANNELS)[number];

export function isConsentableChannel(
  channel: NotificationChannel,
): channel is ConsentableNotificationChannel {
  return (CONSENTABLE_NOTIFICATION_CHANNELS as readonly string[]).includes(channel);
}

/**
 * Through what a consent was given or withdrawn — the "how" KVKK's record of
 * consent has to carry beside the who and the when. `portal_account` is the
 * donor's own hand in the donor area; `admin` is an operator recording what a
 * donor told them, and is the only source that names a user; `public_form` is
 * the tick on the donation site's checkout; `migrated_blanket_consent` is the
 * single `donors.communication_consent` flag this table replaced, carried
 * forward at migration `0073` so that nobody's standing agreement to e-mail was
 * silently dropped.
 */
export const DONOR_CONSENT_SOURCES = [
  'portal_account',
  'admin',
  'public_form',
  'migrated_blanket_consent',
] as const;
export type DonorConsentSource = (typeof DONOR_CONSENT_SOURCES)[number];

/** The channels a row is delivered to by a job, rather than by being written. */
export const DISPATCHED_NOTIFICATION_CHANNELS = ['email', 'sms', 'whatsapp', 'push'] as const;
export type DispatchedNotificationChannel = (typeof DISPATCHED_NOTIFICATION_CHANNELS)[number];

export function isDispatchedChannel(
  channel: NotificationChannel,
): channel is DispatchedNotificationChannel {
  return channel !== 'in_app';
}

/**
 * How often a notification may reach one person on one channel. `immediate` and
 * `off` are what §7.1 delivers; a digest frequency joins the set with §7.2's
 * digest job, which is what would spend the held rows, and is deliberately not
 * declared before anything can spend them.
 */
export const NOTIFICATION_FREQUENCIES = ['immediate', 'off'] as const;
export type NotificationFrequency = (typeof NOTIFICATION_FREQUENCIES)[number];

/**
 * Who a notification is addressed to. The two are not one list with a nullable
 * column: a donor is not a `users` row and never will be, and the two are read,
 * scoped and preferred separately — docs/decisions/0054 §3.
 *
 * `evidence_recipient` is the third, and the first who is neither: a party a
 * donation names and an operator declared on its behalf — today the reference
 * person — reached at the telephone the donation carries, on the basis the
 * `donation_evidence_recipients` row records, with no account, no preferences
 * and no donor consent to read
 * (docs/decisions/0075-evidence-travels-as-an-expiring-link.md).
 */
export const NOTIFICATION_AUDIENCES = ['user', 'donor', 'evidence_recipient'] as const;
export type NotificationAudience = (typeof NOTIFICATION_AUDIENCES)[number];

/**
 * Where a row stands. **E-mail is at least once; SMS, WhatsApp and push are at
 * most once** — the row moves to `sending` before the provider call, and a job
 * that finds it there marks it `delivery_unknown` and never asks again
 * (docs/decisions/0043-the-worker.md's catalogue). `suppressed` is a recipient
 * whose preference is `off`, or who has no address on that channel: it is
 * written rather than dropped, so a question about why somebody was not told is
 * answered by a row.
 */
export const NOTIFICATION_STATUSES = [
  'pending',
  'sending',
  'sent',
  'failed',
  'delivery_unknown',
  'suppressed',
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

/** A status a delivery can no longer move from: the row is finished either way. */
export const SETTLED_NOTIFICATION_STATUSES = [
  'sent',
  'failed',
  'delivery_unknown',
  'suppressed',
] as const;

/**
 * Why a recipient was written down and not told. `no_consent` is a donor who has
 * not agreed to be contacted: KVKK consent is a property of the donor, not of
 * the message, so it is asked once here rather than at each call site, and the
 * row is written so that "why was this donor not told" has an answer. Their
 * `in_app` rows are still written — the donor area is a place they came to.
 * `withdrawn` is a message whose link was revoked between being written and
 * being sent — the party opted out, or the donor was erased — so the delivery
 * stops at the last moment rather than sending a link that opens nothing.
 * `donation_deleted` is a message about a donation put in the trash between
 * being written and being sent: the delivery asks at the last moment, so a
 * notice already queued for a deleted donation never leaves
 * (docs/decisions/0078, 0080).
 */
export const NOTIFICATION_SUPPRESSIONS = [
  'preference_off',
  'no_destination',
  'no_consent',
  'withdrawn',
  'donation_deleted',
] as const;
export type NotificationSuppression = (typeof NOTIFICATION_SUPPRESSIONS)[number];

/**
 * A driver's classified refusal. The code is the whole of what a log line, a
 * `job_failures` row and the operator's screen carry: a provider's own message
 * quotes the number it refused to write to, and that is a donor's telephone
 * number — .claude/rules/secrets.md and docs/decisions/0043-the-worker.md §6.
 */
export const NOTIFICATION_FAILURE_CODES = [
  'NOTIFICATION_PROVIDER_UNCONFIGURED',
  'NOTIFICATION_PROVIDER_UNAVAILABLE',
  'NOTIFICATION_PROVIDER_REFUSED',
  'NOTIFICATION_DESTINATION_INVALID',
  'NOTIFICATION_TEMPLATE_UNAPPROVED',
  'NOTIFICATION_DELIVERY_UNKNOWN',
  'NOTIFICATION_GATEWAY_INSTANCE_UNKNOWN',
  'NOTIFICATION_GATEWAY_AWAITING_LINK',
  'NOTIFICATION_GATEWAY_SESSION_CLOSED',
  'NOTIFICATION_AUTHENTICATION_NOT_ALLOWED',
  'NOTIFICATION_SENDER_UNSEALABLE',
  'NOTIFICATION_LINK_UNAVAILABLE',
] as const;
export type NotificationFailureCode = (typeof NOTIFICATION_FAILURE_CODES)[number];

/**
 * A gateway that drives an ordinary WhatsApp number over the WhatsApp Web
 * protocol, linked by scanning a QR code, is **unofficial infrastructure**: it
 * holds that number's whole session, the number can be banned between one
 * message and the next, and the organization has no support case to open when
 * it is. So it carries notices and never a credential — a one-time code, a
 * sign-in link or a password invitation sent that way locks people out of the
 * system the moment the number goes, which is exactly what the legacy's
 * WhatsApp OTP would have done (docs/plans/09-legacy-parity-integrations.md
 * §9.4, §9.6).
 *
 * The names the legacy's credential messages would carry forward as, refused by
 * name so that adding such an event to the catalogue does not quietly open the
 * channel to it.
 */
export const CREDENTIAL_NOTIFICATION_TEMPLATES = [
  'btsis_sign_in_code',
  'btsis_sign_in_link',
  'btsis_two_factor_code',
  'btsis_password_invitation',
  'btsis_password_reset',
  'btsis_donor_sign_in_link',
] as const;

type EventDefinition = {
  audience: NotificationAudience;
  /** Which channels this event may ever use, and the order a preference is read in. */
  channels: readonly NotificationChannel[];
  /** The channels a recipient gets unless they have said otherwise. */
  defaults: readonly NotificationChannel[];
  /**
   * The placeholders the Turkish templates of this event may fill, and exactly
   * those: `interpolate` throws on one it was given no value for, so the
   * catalogue and the catalogue's strings are checked against each other rather
   * than found out by a donor reading `{{receiptNo}}`.
   */
  variables: readonly string[];
  /**
   * The pre-approved WhatsApp Business template this event is sent as. Every
   * message outside a 24-hour service window must be one, and a template
   * rejected for wording restarts the approval, so the name is a design input
   * here rather than a detail in the driver — §7.1.
   */
  whatsappTemplate: string;
  /**
   * The variables the approved WhatsApp template numbers, in its order, where
   * they are not all of `variables`. A template is fixed text approved at
   * Meta, and Meta refuses a parameter that is empty, so a value that is
   * sometimes absent — the on-behalf-of name — cannot be one of its
   * parameters: the e-mail and SMS text choose a variant without it, and the
   * template is worded to need none.
   */
  whatsappParameters?: readonly string[];
};

/**
 * PRD §7's events. Each is named here whether or not anything raises it yet:
 * the catalogue is what §7.2 wires triggers to, and a template approved at Meta
 * takes weeks, so the names and the template catalogue are settled first.
 *
 * `variables` are ids, codes, dates, names and amounts a recipient is entitled
 * to read about themselves — never another person's data, and never a token.
 */
export const NOTIFICATION_EVENTS = {
  /** §5.6 — a receipt was issued to a donor, and their own area now holds it. */
  'receipt.issued': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['receiptNo', 'amount', 'organizationName', 'issuedOn'],
    whatsappTemplate: 'btsis_receipt_issued',
  },
  /** §6.4 — a recurring collection was refused, and the standing gift has stopped. */
  'pledge.charge_failed': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['amount', 'dueDate', 'campaignName', 'organizationName'],
    whatsappTemplate: 'btsis_pledge_charge_failed',
  },
  /** §6.4 — a standing gift ended, by the donor's own hand or an operator's. */
  'pledge.cancelled': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['amount', 'campaignName', 'organizationName'],
    whatsappTemplate: 'btsis_pledge_cancelled',
  },
  /**
   * §7.2 — the donor's own gift is on the books: the record was saved and
   * counts toward the period — since docs/decisions/0077 the same moment —
   * which is when the legacy's `bts_kayit_detayli` told them about it. Told
   * **once per donation**, by a read of the outbox the way
   * `campaign.goal_reached` asks whether a goal has already been announced.
   *
   * A staff entry tells the payer only when *Bildirim İşlemleri* ticked them —
   * by default it does — on the channel the tick names, **without** asking the
   * donor's channel consent: a transactional message about their own gift
   * (docs/decisions/0080). A portal capture, which nobody ticked, still asks
   * the donor's own consent (docs/decisions/0061).
   *
   * The legacy carried the donor's own telephone number and the referrer's name
   * in the body, and sent the same message to the referrer and to the operator
   * who typed it. The fan-out came back with 0080, each party its own event
   * below; the numbers did not. A notification names what its
   * recipient is entitled to read about themselves, and a donor's number
   * printed in a third party's message is the kind of thing 0061 exists to
   * stop. `quantity` is not among the variables either: it is nullable on a
   * monetary gift, and Meta refuses a template parameter that is empty, so a
   * value that is sometimes absent cannot appear in an approved body — the
   * campaign's name says what was given and the amount says how much.
   */
  'donation.recorded': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['referenceCode', 'campaignName', 'amount', 'organizationName'],
    whatsappTemplate: 'btsis_donation_recorded',
  },
  /**
   * §4.4 — `donation.recorded` for a donation that took no money: the same
   * moment and the same once-per-donation rule, **without an amount**. A
   * request that costs nothing has none to state, and "₺0,00 tutarındaki
   * bağışınız" would tell the donor something false. The writer chooses it by
   * the donation's own `not_charged` payment status, never by its campaign —
   * docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md.
   */
  'donation.request_recorded': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['referenceCode', 'campaignName', 'organizationName'],
    whatsappTemplate: 'btsis_donation_request_recorded',
  },
  /**
   * *KAYIT BİLDİRİMİ* to ***Bağış Sahibi***, when staff ticked them: a gift
   * was recorded in their name. It names the campaign and the reference code
   * and nobody else — not the payer, whose gift it is to announce
   * (docs/decisions/0080).
   */
  'donation.recorded_owner': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['whatsapp'],
    variables: ['referenceCode', 'campaignName', 'organizationName'],
    whatsappTemplate: 'btsis_donation_recorded_owner',
  },
  /**
   * *KAYIT BİLDİRİMİ* to **the reference person**, when staff ticked them,
   * at the telephone the donation carries, on the operator's declaration —
   * the audience 0075 made for them. It names the campaign and the dedication,
   * never the payer or an amount: the reference introduced the gift and did
   * not pay it (docs/decisions/0080).
   */
  'donation.recorded_reference': {
    audience: 'evidence_recipient',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['whatsapp'],
    variables: ['campaignName', 'onBehalfOf', 'organizationName'],
    whatsappTemplate: 'btsis_donation_recorded_reference',
    whatsappParameters: ['campaignName', 'organizationName'],
  },
  /**
   * *KAYIT BİLDİRİMİ* to **the operator the donation is recorded against**,
   * when ticked: the entry they typed, or one typed for them, is on the books.
   * At their own telephone (migration `0107`) or address, on the channel the
   * tick names — docs/decisions/0080.
   */
  'donation.recorded_operator': {
    audience: 'user',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['whatsapp'],
    variables: ['referenceCode', 'campaignName', 'onBehalfOf', 'organizationName'],
    whatsappTemplate: 'btsis_donation_recorded_operator',
    whatsappParameters: ['referenceCode', 'campaignName', 'organizationName'],
  },
  /**
   * §7.2 — the thing the donor paid for actually happened: the donation reached
   * `fulfilled`, which is the one state in this model that means carried out,
   * and which the database will not let stand without a `fulfilled_at`, a
   * `fulfilled_institution_id` and a human `fulfilled_by_user_id`. For a
   * sacrifice or a distribution this is the moment that matters to the donor,
   * and it is what the legacy's `bagis_gerceklesti_detayli` carried.
   *
   * `location` is the institution that carried it out — the legacy's
   * `gerceklestigi_altbolge`, which it also told the donor. What does **not**
   * carry forward is the legacy's media button: it pointed at a public
   * `/media/{code}` gallery of the fulfilment's photographs that anyone holding
   * a reference code could open for ever. The photographs travel in
   * `donation.evidence_ready` instead, as an expiring, unguessable link per
   * party — docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
   */
  'donation.fulfilled': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp', 'in_app'],
    defaults: ['email'],
    variables: ['referenceCode', 'campaignName', 'fulfilledOn', 'location', 'organizationName'],
    whatsappTemplate: 'btsis_donation_fulfilled',
  },
  /**
   * §3.1 step 3 and §4.4 — **the donation's fulfilment evidence is ready, and
   * this is the link to it**, to the payer, when the donation is carried out
   * with a clean photograph or video, or when the first one arrives after. It
   * is sent **on the channel the payer's choice row names, without asking the
   * donor's channel consent** — *GERÇEKLEŞTİ (GÖRSEL/KANIT) BİLDİRİMİ*, a
   * transactional message staff ticked (docs/decisions/0080) — once per
   * choice row, however often it is raised. `link` is filled at delivery
   * by an expiring, unguessable token and is never stored: the row carries
   * `EVIDENCE_LINK_PLACEHOLDER` in its place. The message names the campaign
   * and the dedication and nothing about the payer —
   * docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
   */
  'donation.evidence_ready': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['email', 'sms', 'whatsapp'],
    variables: ['campaignName', 'onBehalfOf', 'organizationName', 'link'],
    whatsappTemplate: 'btsis_donation_evidence_ready',
    whatsappParameters: ['campaignName', 'organizationName', 'link'],
  },
  /**
   * The same evidence, to the **reference person** an operator named and
   * declared on the reference person's behalf — a party with no account and no
   * donor consent to read, reached at the telephone the donation carries on the
   * operator's declaration (docs/decisions/0070 §5). Its own event, because a
   * stranger is addressed differently from the person who paid, and because a
   * WhatsApp template is approved per wording.
   */
  'donation.evidence_ready_reference': {
    audience: 'evidence_recipient',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['email', 'sms', 'whatsapp'],
    variables: ['campaignName', 'onBehalfOf', 'organizationName', 'link'],
    whatsappTemplate: 'btsis_donation_evidence_ready_reference',
    whatsappParameters: ['campaignName', 'organizationName', 'link'],
  },
  /**
   * The same evidence, to ***Bağış Sahibi*** — the person the gift was made
   * on behalf of, when the donation names them as a donor
   * (docs/decisions/0079) and staff ticked them. Its own event, because the
   * gift is theirs but the payment is not: the message says it was made in
   * their name and names nobody else — docs/decisions/0080.
   */
  'donation.evidence_ready_owner': {
    audience: 'donor',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['email', 'sms', 'whatsapp'],
    variables: ['campaignName', 'organizationName', 'link'],
    whatsappTemplate: 'btsis_donation_evidence_ready_owner',
  },
  /**
   * The same evidence, to **the operator who entered the donation**, on the
   * channel their choice row names: since docs/decisions/0080 a telephone
   * (migration `0107`), and e-mail for every row chosen before it, which is
   * what 0075 sent. Never the centre: a link carries a token, and a row the
   * centre shows would have to store it.
   */
  'donation.evidence_ready_operator': {
    audience: 'user',
    channels: ['email', 'sms', 'whatsapp'],
    defaults: ['email'],
    variables: ['campaignName', 'onBehalfOf', 'organizationName', 'link'],
    whatsappTemplate: 'btsis_donation_evidence_ready_operator',
    whatsappParameters: ['campaignName', 'organizationName', 'link'],
  },
  /** §6.3 — a person answered the donor's KVKK request. No route writes it yet. */
  'donor.privacy_request_answered': {
    audience: 'donor',
    channels: ['email', 'in_app'],
    defaults: ['email'],
    variables: ['kind', 'organizationName'],
    whatsappTemplate: 'btsis_privacy_request_answered',
  },
  /**
   * §4.4 and §7.2 — a donation was assigned to the institution that will carry
   * it out, and the people who manage that assignment are told: when a
   * donation naming its yurt is saved, and whenever a counted donation is moved to
   * another yurt or season — by a correction or by queueing it somewhere else.
   * It is the institution being told **on the donor's behalf**, for every
   * campaign's assigned donations, not only a hatim's. Told **once per
   * manager per donation**, by a read of the outbox, so a correction or a
   * queue to the yurt it already names tells nobody twice.
   *
   * It replaced `donation.queued`, which §7.2 catalogued for the same person at
   * a narrower moment and nothing ever raised; migration `0097` carries a
   * preference anybody set for it over. The variables are what the institution
   * needs to act — the reference code, the campaign, which of its assignments
   * and whom the work is on behalf of — and **never the payer's name or
   * telephone**, which the institution has no business reading. A user has no
   * telephone here, so there is no WhatsApp channel and no template to approve.
   *
   * E-mail is a default beside the centre, unlike the other staff notices:
   * this one is work to be done at a yurt whose managers may not live in the
   * panel, and a notice of work that waits unread is the failure it exists to
   * prevent — docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md.
   */
  'donation.assigned': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['email', 'in_app'],
    variables: ['referenceCode', 'campaignName', 'institutionName', 'onBehalfOf'],
    whatsappTemplate: 'btsis_donation_assigned',
  },
  /** §7.2 — a bank line matched nothing anybody could see. */
  'reconciliation.unmatched': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['in_app'],
    variables: ['amount', 'bookedOn', 'accountName'],
    whatsappTemplate: 'btsis_reconciliation_unmatched',
  },
  /** §7.2 — a match was proposed and is waiting for somebody to approve it. */
  'reconciliation.awaiting_approval': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['in_app'],
    variables: ['amount', 'referenceCode', 'bookedOn'],
    whatsappTemplate: 'btsis_reconciliation_awaiting_approval',
  },
  /** §7.2 — a campaign period reached what it set out to collect. */
  'campaign.goal_reached': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['in_app'],
    variables: ['campaignName', 'periodName', 'amount'],
    whatsappTemplate: 'btsis_campaign_goal_reached',
  },
  /**
   * §12.5 — a user was put on a campaign's staff at their institution, or an
   * assignment naming them came back into force. Told in the centre by default,
   * as a permission change is: the assignment is work in the panel, and a
   * person who wants it by mail turns it on. It names the campaign, the
   * institution and who assigned them, and nothing else —
   * docs/decisions/0089.
   */
  'campaign.staff_assigned': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['in_app'],
    variables: ['campaignName', 'institutionName', 'assignedBy'],
    whatsappTemplate: 'btsis_campaign_staff_assigned',
  },
  /**
   * §7.2 — what a user may reach changed, which they are told rather than
   * discover. **The centre is its default and e-mail is not**: the change takes
   * effect where they work, and a mail about every role edit is the kind of
   * notice people learn to filter. Somebody who wants it by mail turns it on.
   */
  'user.permissions_changed': {
    audience: 'user',
    channels: ['email', 'in_app'],
    defaults: ['in_app'],
    variables: ['changedBy'],
    whatsappTemplate: 'btsis_user_permissions_changed',
  },
} as const satisfies Record<string, EventDefinition>;

export type NotificationEvent = keyof typeof NOTIFICATION_EVENTS;

export const NOTIFICATION_EVENT_NAMES = Object.keys(NOTIFICATION_EVENTS) as NotificationEvent[];

export function isNotificationEvent(value: string): value is NotificationEvent {
  return Object.hasOwn(NOTIFICATION_EVENTS, value);
}

/** The values an approved WhatsApp template numbers, in the order it numbers them. */
export function whatsappParametersOf(event: NotificationEvent): readonly string[] {
  const definition: EventDefinition = NOTIFICATION_EVENTS[event];
  return definition.whatsappParameters ?? definition.variables;
}

/**
 * **The `onBehalfOf` a notice carries: the name a gift was dedicated to, or
 * the empty string when there is none to print** — a gift nobody was named
 * for. Never a stand-in word: a template reading
 * "Belirtilmedi adına …" printed the placeholder as though it were a person.
 * The renderer chooses the template's `unnamed` variant, which has no
 * "… adına" clause, whenever this is empty.
 */
export function onBehalfOfOf(donation: { onBehalfOfName: string | null }): string {
  return donation.onBehalfOfName?.trim() ?? '';
}

export type NotificationVariables<Event extends NotificationEvent = NotificationEvent> = Record<
  (typeof NOTIFICATION_EVENTS)[Event]['variables'][number],
  string | number
>;

/**
 * Whether a message may leave over an unofficial gateway. It is an **allowlist
 * of the event catalogue's own templates**, minus the credential names above,
 * so a row whose event this build does not know — one written by a newer
 * release, or a template assembled anywhere but the catalogue — is refused
 * rather than sent. Nothing that authenticates has a template here to be
 * allowed by.
 */
export function mayLeaveOverUnofficialGateway(templateName: string): boolean {
  return UNOFFICIAL_GATEWAY_TEMPLATES.has(templateName);
}

const UNOFFICIAL_GATEWAY_TEMPLATES: ReadonlySet<string> = new Set(
  Object.values(NOTIFICATION_EVENTS)
    .map((definition) => definition.whatsappTemplate as string)
    .filter((name) => !(CREDENTIAL_NOTIFICATION_TEMPLATES as readonly string[]).includes(name)),
);

/** The channels this event may use, narrowed to the ones the recipient has not turned off. */
export function channelsFor(
  event: NotificationEvent,
  chosen: ReadonlyMap<NotificationChannel, NotificationFrequency>,
): NotificationChannel[] {
  const definition: EventDefinition = NOTIFICATION_EVENTS[event];
  return definition.channels.filter((channel) => {
    const frequency = chosen.get(channel);
    return frequency === undefined ? definition.defaults.includes(channel) : frequency !== 'off';
  });
}

/**
 * The channels this event would have used and the recipient has **said no to** —
 * an explicit `off`, never a channel that is simply not one of the event's
 * defaults. The two are not the same thing, and only the first is an answer to
 * "why was this person not told": a row is written for it, `suppressed` with
 * `preference_off`, so a withdrawal is visible on the operator's screen rather
 * than being an absence of rows.
 */
export function refusedChannelsFor(
  event: NotificationEvent,
  chosen: ReadonlyMap<NotificationChannel, NotificationFrequency>,
): NotificationChannel[] {
  const definition: EventDefinition = NOTIFICATION_EVENTS[event];
  return definition.channels.filter((channel) => chosen.get(channel) === 'off');
}

/**
 * Whether a donor may be offered a channel at all — the answer both screens
 * render and both writers enforce, so an operator and a donor are never shown
 * a switch that would write a row the database refuses.
 *
 * A channel with no address is **not** merely unlikely to be chosen: there is
 * nowhere to send to, so choosing it would be a promise the system cannot keep,
 * and `notification_preferences_require_reachable_donor` refuses the row.
 */
export function donorChannelOfferable(
  channel: NotificationChannel,
  addresses: { email: string | null; phone: string | null },
): boolean {
  if (channel === 'email') return addresses.email !== null && addresses.email.trim() !== '';
  if (channel === 'sms' || channel === 'whatsapp') return addresses.phone !== null;
  return false;
}

/**
 * A destination as a log line, an operator's list or a message to the shared
 * operations mailbox may show it — never in full. An address keeps its first
 * characters and its domain's last label; a number keeps its country prefix and
 * its last two digits, which is enough for its owner to recognise and not
 * enough for a reader of the screen to write to.
 */
export function maskDestination(channel: NotificationChannel, destination: string): string {
  if (channel === 'email') {
    const at = destination.lastIndexOf('@');
    if (at <= 0) return '***';
    const local = destination.slice(0, at);
    const labels = destination.slice(at + 1).split('.');
    const suffix = labels.length > 1 ? `.${labels.at(-1)}` : '';
    const host = labels.length > 1 ? labels.slice(0, -1).join('.') : (labels[0] ?? '');
    return `${local.slice(0, local.length > 3 ? 2 : 1)}***@${host.slice(0, 1)}***${suffix}`;
  }
  const digits = destination.replace(/[^\d+]/g, '');
  if (digits.length < 6) return '***';
  return `${digits.slice(0, 3)}***${digits.slice(-2)}`;
}

/**
 * A telephone number in E.164 — what every provider this phase carries is given
 * and what the column stores, so a Turkish `0532…`, a spaced `+90 532 …` and a
 * `0090…` are one destination rather than three. A national number is read
 * against `defaultCountry`, Türkiye unless a caller knows better. A number that
 * cannot be read as one is not guessed at: the row is suppressed as
 * `no_destination`. It is `normalizePhone` (docs/decisions/0086).
 */
export function toE164(
  value: string,
  defaultCountry: string = DEFAULT_PHONE_COUNTRY,
): string | null {
  return normalizePhone(value, defaultCountry);
}

/**
 * The providers an organization may hold a sender credential for — the two
 * active transports. E-mail is not among them: every organization's mail leaves
 * over the deployment's one SMTP relay, which is infrastructure rather than a
 * per-organization account, and `in_app` is delivered by being written.
 */
export const NOTIFICATION_SENDER_PROVIDERS = ['netgsm', 'whatsapp_business'] as const;
export type NotificationSenderProvider = (typeof NOTIFICATION_SENDER_PROVIDERS)[number];

export function isNotificationSenderProvider(value: string): value is NotificationSenderProvider {
  return (NOTIFICATION_SENDER_PROVIDERS as readonly string[]).includes(value);
}

/** The channels an organization may hold its own sender for. */
export const SENDER_CHANNELS = ['sms', 'whatsapp'] as const;
export type SenderChannel = (typeof SENDER_CHANNELS)[number];

/** Supported delivery transports: Netgsm for SMS, Meta Cloud API for WhatsApp. */
export const NOTIFICATION_SENDER_PRECEDENCE = {
  sms: ['netgsm'],
  whatsapp: ['whatsapp_business'],
} as const satisfies Record<SenderChannel, readonly NotificationSenderProvider[]>;

/** The channels one active provider can carry. */
export function channelsOfSenderProvider(
  provider: NotificationSenderProvider,
): readonly SenderChannel[] {
  return SENDER_CHANNELS.filter((channel) =>
    (NOTIFICATION_SENDER_PRECEDENCE[channel] as readonly string[]).includes(provider),
  );
}

/**
 * The **non-secret** fields each provider's sender row carries, exactly. A
 * request that omits one or adds another is a half-configured provider, refused
 * by the service and, under it, by
 * `organization_notification_senders_public_ref_matches_provider` — so a row a
 * driver could not be built from cannot exist even if a caller reached Postgres
 * another way.
 */
export const NOTIFICATION_SENDER_PUBLIC_FIELDS = {
  netgsm: ['userCode', 'msgHeader', 'apiBaseUrl'],
  whatsapp_business: ['phoneNumberId', 'apiBaseUrl', 'apiVersion', 'languageCode'],
} as const satisfies Record<NotificationSenderProvider, readonly string[]>;

/**
 * The **secret** fields each provider's sender row seals, exactly. A Meta access
 * token and a Netgsm password are not the same kind of thing, but both are the
 * one value the provider authenticates on, and neither is ever returned.
 */
export const NOTIFICATION_SENDER_SECRET_FIELDS = {
  netgsm: ['password'],
  whatsapp_business: ['accessToken'],
} as const satisfies Record<NotificationSenderProvider, readonly string[]>;

/** The one secret a provider's fingerprint is taken from — its primary credential. */
export function primarySecretFieldOf(provider: NotificationSenderProvider): string {
  return NOTIFICATION_SENDER_SECRET_FIELDS[provider][0];
}

/**
 * Which provider carries a channel for a set of configured ones — the first of
 * the channel's precedence that is present, and `null` where none is. It is one
 * function so that the screen's summary, the worker's driver lookup and the
 * global environment set cannot answer the same question differently.
 */
export function chooseSenderProvider(
  channel: SenderChannel,
  available: ReadonlySet<NotificationSenderProvider>,
): NotificationSenderProvider | null {
  return (
    NOTIFICATION_SENDER_PRECEDENCE[channel].find((provider) => available.has(provider)) ?? null
  );
}
